import { useState } from 'react';

/**
 * GenreSubgenreFilter — Control B, from `MatchMe_Filters`
 * (docs/genre-subgenre-build-spec.md, Part 3B / PR 3).
 *
 * "Is this person describing a BOOK, or describing THEMSELVES?" Control B is
 * for the second case: describing a person or a craving. It is canon-only —
 * no write-in. Taste never mints vocabulary; a reader typing a new subgenre
 * while filtering would create catalogue rows nobody curated. That is the
 * one real difference from Control A (GenreSubgenrePicker) — everything else
 * about the two-level shape is the same.
 *
 * Genre chips are multi-select, flat. Picking a genre auto-opens its
 * subgenre accordion; only ONE genre's accordion is open at a time (opening
 * one closes whichever was open), and a closed row keeps its own "N picked"
 * badge so a choice already made is not hidden by collapsing the row. A
 * genre with nothing picked under it is a complete, valid filter on its own.
 *
 * This component owns none of the taxonomy fetch or the chip's own look —
 * both are passed in, the same way SubgenreSection (reanlo-author,
 * AddBook.jsx) receives canonSubgenresForGenre/matchIndex from whichever
 * screen called useGenreTaxonomy(), and the same way every reader surface
 * this control lands on brings its own Chip so this stays one component
 * rather than one per surface's visual style.
 *
 * @param {Object} props
 * @param {React.ComponentType<{on:boolean, onClick:()=>void, children:*}>} props.Chip
 * @param {Array<{id:number,name:string}>} props.genres  ordered by sort_order
 * @param {(genreId:number) => Array<{id:number,name:string,genre_id:number}>} props.canonSubgenresForGenre
 * @param {boolean} [props.loading]
 * @param {string} [props.error]
 * @param {number[]} props.selectedGenreIds
 * @param {(updater: number[] | ((prev:number[])=>number[])) => void} props.setSelectedGenreIds
 * @param {number[]} props.selectedSubgenreIds
 * @param {(updater: number[] | ((prev:number[])=>number[])) => void} props.setSelectedSubgenreIds
 */
export default function GenreSubgenreFilter({
  Chip,
  genres,
  canonSubgenresForGenre,
  loading = false,
  error = '',
  selectedGenreIds,
  setSelectedGenreIds,
  selectedSubgenreIds,
  setSelectedSubgenreIds,
}) {
  // Which genre's accordion is open. One at a time, by design — see the
  // component docblock. null means none open.
  const [openGenreId, setOpenGenreId] = useState(null);

  function toggleGenre(id) {
    if (selectedGenreIds.includes(id)) {
      // Unchecking a genre also drops its subgenres, same reasoning as
      // AddBook.jsx's GenreSubgenrePicker: a subgenre's parent is derived
      // from the subgenre row, never stored twice, so holding a Fantasy
      // subgenre while Fantasy itself is unchecked has no coherent meaning.
      const dropIds = new Set(canonSubgenresForGenre(id).map(s => s.id));
      setSelectedSubgenreIds(prev => prev.filter(sid => !dropIds.has(sid)));
      setSelectedGenreIds(prev => prev.filter(g => g !== id));
      setOpenGenreId(prev => (prev === id ? null : prev));
    } else {
      setSelectedGenreIds(prev => [...prev, id]);
      // Picking a genre auto-opens it and closes whatever else was open —
      // the accordion is "get specific right away", not a second step.
      setOpenGenreId(id);
    }
  }

  function toggleSubgenre(id) {
    setSelectedSubgenreIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }

  if (error) {
    return (
      <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '10px 0 0' }}>
        Couldn't load the genre list right now, {error}
      </p>
    );
  }

  if (loading) {
    return (
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: '10px 0 0' }}>
        Loading genres…
      </p>
    );
  }

  if (genres.length === 0) {
    return (
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontStyle: 'italic', margin: '10px 0 0' }}>
        No genres tagged yet.
      </p>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
        {genres.map(g => (
          <Chip key={g.id} on={selectedGenreIds.includes(g.id)} onClick={() => toggleGenre(g.id)}>{g.name}</Chip>
        ))}
      </div>

      {selectedGenreIds.map(genreId => {
        const genre = genres.find(g => g.id === genreId);
        if (!genre) return null; // taxonomy still loading, or a stale id round-tripped from initialFilters

        const canon = canonSubgenresForGenre(genreId);
        const pickedHere = canon.filter(s => selectedSubgenreIds.includes(s.id));
        const isOpen = openGenreId === genreId;

        return (
          <div key={genreId} style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setOpenGenreId(isOpen ? null : genreId)}
              aria-expanded={isOpen}
              style={{
                width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center',
                gap: 'var(--space-sm)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                background: isOpen ? 'var(--brand-cream)' : 'var(--brand-white)',
                border: `1.5px solid ${isOpen ? 'var(--brand-gold)' : 'var(--brand-sand)'}`,
                borderRadius: 'var(--radius-md)', padding: '8px 13px',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>{genre.name}</span>
              {pickedHere.length > 0 && (
                <span style={{
                  background: 'var(--brand-forest)', color: 'var(--brand-white)', borderRadius: 'var(--radius-pill)',
                  padding: '1px 8px', fontSize: 'var(--font-size-xxs)', fontWeight: 800,
                }}>{pickedHere.length} picked</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontWeight: 700 }}>
                {isOpen ? 'close ▴' : 'get specific ▾'}
              </span>
            </button>

            {isOpen && (
              <div style={{
                border: '1.5px solid var(--brand-gold)', borderTop: 'none', borderRadius: '0 0 var(--radius-md) var(--radius-md)',
                padding: '10px 13px 12px', margin: '0 6px', background: 'var(--brand-cream)',
              }}>
                {canon.length === 0 ? (
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontStyle: 'italic', margin: 0 }}>
                    No subgenres listed under {genre.name} yet.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {canon.map(s => (
                      <Chip key={s.id} on={selectedSubgenreIds.includes(s.id)} onClick={() => toggleSubgenre(s.id)}>{s.name}</Chip>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginTop: 8 }}>
        A genre alone is a valid pick, a subgenre narrows within its genre and implies it.
      </p>
    </div>
  );
}

/**
 * The names an applied filter should search on, given the ids this
 * component's caller is holding. Both genre and subgenre names go into the
 * SAME flat facet: matching_book_ids' genre_tags parameter matches
 * book_tags/tropes by canonical_name (docs/genre-subgenre-build-spec.md's
 * PR 3 scope is "SQL: none" precisely because it doesn't have to change —
 * the author form's dual-write already writes every picked genre AND
 * subgenre name into book_tags as tag_type='genre', kept on purpose until
 * PR 5). So a subgenre name finds the books tagged with it exactly the way
 * a genre name already does; nothing here needs its own facet parameter.
 *
 * @param {number[]} selectedGenreIds
 * @param {number[]} selectedSubgenreIds
 * @param {Array<{id:number,name:string}>} genres
 * @param {Map<number,{id:number,name:string}>} subgenresById
 * @returns {string[]}
 */
export function genreFilterNames(selectedGenreIds, selectedSubgenreIds, genres, subgenresById) {
  const genresById = new Map(genres.map(g => [g.id, g]));
  const genreNames = selectedGenreIds.map(id => genresById.get(id)?.name).filter(Boolean);
  const subgenreNames = selectedSubgenreIds.map(id => subgenresById.get(id)?.name).filter(Boolean);
  return [...genreNames, ...subgenreNames];
}
